/**
 * How long to wait before asking for the client config again, and when to stop.
 *
 * Its own module, and not part of `session.ts`, for the same reason
 * `voice-roster.ts` is not part of `voice.ts`: everything in `session.ts`
 * reaches the Nakama client and the build-time environment, and none of that is
 * reachable from a test. This is a schedule of numbers, so it should be.
 *
 * ## Why there is a retry at all
 *
 * A failed config fetch used to be permanent. It was caught and dropped with a
 * comment saying the screens that needed it would ask again — and **nothing
 * ever asked again.** One blip while the app was starting, such as the server
 * restarting underneath a page that was loading, left the config null for the
 * whole page load.
 *
 * What made it hard to see is how little it broke. The avatars and the phrase
 * list arrive with `join_room`, so a game dealt and played normally. The only
 * casualties were the turn-limit choices and **the voice button, which was
 * simply absent** — indistinguishable from voice being switched off for the
 * room, and so not obviously a fault at all.
 *
 * Bounded rather than forever: four tries over about sixteen seconds covers a
 * restart or a moment of bad signal, and a client that has failed five times is
 * not going to be rescued by a sixth.
 */

export const CONFIG_RETRY_DELAYS_MS: readonly number[] = [500, 1500, 4000, 10000];

/** The wait before attempt `attempt`, or null when there are none left. */
export function configRetryDelayMs(attempt: number): number | null {
  return CONFIG_RETRY_DELAYS_MS[attempt] ?? null;
}
